import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../utils/api';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import HarvestingTopNav from '../components/HarvestingTopNav';
import './HarvestingMapPage.css';

// Fix for the default marker icon issue in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Component to fit the map to GeoJSON bounds when territory is loaded
const FitBoundsToGeoJSON = ({ geojsonData }) => {
  const map = useMap();
  useEffect(() => {
    if (geojsonData && geojsonData.features && geojsonData.features.length > 0) {
      try {
        const geoJsonLayer = L.geoJSON(geojsonData);
        const bounds = geoJsonLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error('Error fitting map to GeoJSON bounds:', error);
      }
    }
  }, [geojsonData, map]);
  return null;
};

// Color map for harvest submission icons
const HARVEST_COLORS = {
  'harvesting': '#2e7d32',
  'berry-harvesting': '#7b1fa2',
};

const getHarvestIcon = (dataType, item) => {
  const color = HARVEST_COLORS[dataType] || '#388e3c';
  const icon = dataType === 'berry-harvesting' ? '🫐' : '🌿';
  return L.divIcon({
    className: 'harvest-submission-marker',
    html: `<div style="
      background-color: ${color};
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    ">${icon}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
};

const HarvestingMapPage = () => {
  // Static reference zones (hardcoded harvesting areas near Thunder Bay)
  const staticAreas = [
    {
      id: 1,
      name: 'Wild Berry Area',
      location: [48.4534, -89.257],
      description: 'Area with abundant wild berries, available July–September.',
      restrictions: 'Permit required. Maximum 2 kg per person.',
      season: 'Summer',
    },
    {
      id: 2,
      name: 'Medicinal Herb Zone',
      location: [48.392, -89.245],
      description: 'Traditional medicinal herbs can be found in this area.',
      restrictions: 'Elder accompaniment recommended. Sustainable harvesting practices required.',
      season: 'Spring, Summer',
    },
    {
      id: 3,
      name: 'Cedar Collection Area',
      location: [48.375, -89.315],
      description: 'Designated area for cedar bark harvesting.',
      restrictions: 'Special permit required. Cultural use only.',
      season: 'Spring',
    },
  ];

  // Legend collapse state
  const [legendOpen, setLegendOpen] = useState(false);

  // Territory layer
  const [showTerritory, setShowTerritory] = useState(false);
  const [territoryData, setTerritoryData] = useState(null);
  const [territoryLoading, setTerritoryLoading] = useState(false);
  const [territoryError, setTerritoryError] = useState(null);

  // Live harvest submissions from RS DB
  const [harvestSubmissions, setHarvestSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);
  const [submissionsError, setSubmissionsError] = useState(null);

  const defaultCenter = [48.3809, -89.2477]; // Thunder Bay, Ontario
  const defaultZoom = 11;

  const territoryStyle = {
    fillColor: '#FF4500',
    weight: 3,
    opacity: 0.8,
    color: '#FF0000',
    fillOpacity: 0.3,
    dashArray: '5, 5',
  };

  // Fetch territory GeoJSON when toggled on
  useEffect(() => {
    if (showTerritory && !territoryData) {
      setTerritoryLoading(true);
      setTerritoryError(null);
      axios
        .get(`${API_URL}/api/territory`)
        .then((res) => {
          setTerritoryData(res.data);
          setTerritoryLoading(false);
        })
        .catch((err) => {
          console.error('Error loading territory:', err);
          setTerritoryError('Failed to load territory data');
          setTerritoryLoading(false);
        });
    }
  }, [showTerritory, territoryData]);

  // Fetch live community harvest submissions (approved/public records with location data)
  useEffect(() => {
    const fetchSubmissions = async () => {
      setSubmissionsLoading(true);
      try {
        const [generalRes, berryRes] = await Promise.allSettled([
          api.get('/api/data/harvesting').catch(() => axios.get(`${API_URL}/api/data/harvesting`)),
          api.get('/api/data/berry-harvesting').catch(() => axios.get(`${API_URL}/api/data/berry-harvesting`)),
        ]);

        const combined = [];

        if (generalRes.status === 'fulfilled') {
          const items = generalRes.value?.data?.items || generalRes.value?.data || [];
          items.forEach((item) => {
            if (item.location && item.location.length === 2) {
              combined.push({ ...item, _dataType: 'harvesting' });
            }
          });
        }

        if (berryRes.status === 'fulfilled') {
          const items = berryRes.value?.data?.items || berryRes.value?.data || [];
          items.forEach((item) => {
            if (item.location && item.location.length === 2) {
              combined.push({ ...item, _dataType: 'berry-harvesting' });
            }
          });
        }

        setHarvestSubmissions(combined);
      } catch (err) {
        console.error('Error fetching harvest submissions:', err);
        setSubmissionsError('Could not load community harvest records');
      } finally {
        setSubmissionsLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  return (
    <div className="harvesting-map-page">
      <HarvestingTopNav />

      <div className="map-header">
        <div className="map-header-top">
          <div>
            <h1>Harvesting Map</h1>
            <p>View seasonal harvesting areas, RSMIN territory, and community harvest records</p>
          </div>
          <div className="map-header-actions">
            <Link to="/data/harvesting" className="btn-submit-harvest">
              + Submit a Harvest
            </Link>
            <Link to="/harvesting" className="btn-back-harvesting">
              ← Back to Harvesting Portal
            </Link>
          </div>
        </div>

        <div className="map-controls">
          <button
            className={`territory-toggle ${showTerritory ? 'active' : ''}`}
            onClick={() => setShowTerritory((v) => !v)}
            disabled={territoryLoading}
          >
            {territoryLoading
              ? 'Loading...'
              : showTerritory
              ? 'Hide RSMIN Territory'
              : 'Show RSMIN Territory'}
          </button>

          <div className="map-status">
            {submissionsLoading && <span className="map-status-loading">Loading community records...</span>}
            {!submissionsLoading && !submissionsError && (
              <span className="map-status-count">
                {harvestSubmissions.length} community harvest{harvestSubmissions.length !== 1 ? 's' : ''} recorded
              </span>
            )}
            {submissionsError && <span className="map-status-error">{submissionsError}</span>}
          </div>
        </div>

        {territoryError && <div className="error-message">{territoryError}</div>}
      </div>

      <div className="map-body">
        <div className="map-panel" style={{ height: '100%', width: '100%' }}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          scrollWheelZoom={true}
          className="leaflet-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Territory overlay */}
          {showTerritory && territoryData && (
            <>
              <GeoJSON
                key={JSON.stringify(territoryData)}
                data={territoryData}
                style={territoryStyle}
                onEachFeature={(feature, layer) => {
                  const name =
                    feature.properties && feature.properties.name
                      ? feature.properties.name
                      : 'RSMIN Territory';
                  layer.bindPopup(name);
                }}
              />
              <FitBoundsToGeoJSON geojsonData={territoryData} />
            </>
          )}

          {/* Static reference harvesting zones */}
          {staticAreas.map((area) => (
            <Marker key={area.id} position={area.location}>
              <Popup>
                <div className="popup-content">
                  <h3>{area.name}</h3>
                  <p>
                    <strong>Description:</strong> {area.description}
                  </p>
                  <p>
                    <strong>Restrictions:</strong> {area.restrictions}
                  </p>
                  <p>
                    <strong>Season:</strong> {area.season}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Live community harvest submissions */}
          {harvestSubmissions.map((item) => (
            <Marker
              key={item._id}
              position={[item.location[0], item.location[1]]}
              icon={getHarvestIcon(item._dataType, item)}
            >
              <Popup>
                <div className="popup-content harvest-submission-popup">
                  <div className="popup-type-badge">
                    {item._dataType === 'berry-harvesting' ? '🫐 Berry Harvest' : '🌿 Community Harvest'}
                  </div>
                  <h3>{item.title || item.createdByName || 'Harvest Record'}</h3>
                  {item.description && <p>{item.description}</p>}
                  {item.structuredData && item.structuredData.abundance && (
                    <p>
                      <strong>Abundance:</strong> {item.structuredData.abundance}
                    </p>
                  )}
                  {item.structuredData && item.structuredData.harvestSeason && (
                    <p>
                      <strong>Season:</strong> {item.structuredData.harvestSeason}
                    </p>
                  )}
                  {item.freeTextData && item.freeTextData.observations && (
                    <p>
                      <strong>Notes:</strong> {item.freeTextData.observations}
                    </p>
                  )}
                  <p className="popup-submitter">
                    Submitted by: {item.createdByName || 'Community Member'}
                  </p>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Floating legend button */}
        <button className="legend-fab" onClick={() => setLegendOpen(true)}>
          📋 Legend
        </button>
        </div>

        <div className="map-sidebar">
          <div className="harvesting-guidelines">
            <h2>Harvesting Guidelines</h2>
            <ul>
              <li>Always obtain proper permits before harvesting</li>
              <li>Practice sustainable harvesting techniques</li>
              <li>Respect seasonal restrictions and quantity limits</li>
              <li>Some areas may require cultural knowledge or elder guidance</li>
              <li>Report any environmental concerns to RSMIN administration</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legend modal */}
      {legendOpen && (
        <div className="legend-modal-overlay" onClick={() => setLegendOpen(false)}>
          <div className="legend-modal" onClick={(e) => e.stopPropagation()}>
            <div className="legend-modal-header">
              <span>📋 Map Legend</span>
              <button className="legend-modal-close" onClick={() => setLegendOpen(false)}>✕</button>
            </div>
            <div className="legend-modal-body">
              <div className="legend-grid">
                <div className="legend-section">
                  <h3>Reference Harvesting Areas</h3>
                  <ul className="legend-items">
                    {staticAreas.map((area) => (
                      <li key={area.id} className="legend-item">
                        <h4>{area.name}</h4>
                        <p><strong>Season:</strong> {area.season}</p>
                        <p><strong>Rules:</strong> {area.restrictions}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="legend-section">
                  <h3>Community Harvests</h3>
                  <ul className="legend-items">
                    <li className="legend-item">
                      <span className="legend-dot" style={{ backgroundColor: '#2e7d32' }}>🌿</span>
                      <span>General Harvest Submission</span>
                    </li>
                    <li className="legend-item">
                      <span className="legend-dot" style={{ backgroundColor: '#7b1fa2' }}>🫐</span>
                      <span>Berry Harvest Submission</span>
                    </li>
                  </ul>
                  <div className="legend-submit-cta">
                    <Link to="/data/harvesting" className="btn-submit-harvest">
                      + Submit Your Harvest
                    </Link>
                  </div>
                </div>

                {showTerritory && (
                  <div className="legend-section">
                    <h3>Territory</h3>
                    <ul className="legend-items">
                      <li className="legend-item territory-legend">
                        <div
                          className="territory-indicator"
                          style={{
                            backgroundColor: territoryStyle.fillColor,
                            opacity: 0.5,
                            border: `3px dashed ${territoryStyle.color}`,
                            width: 24,
                            height: 16,
                            display: 'inline-block',
                            marginRight: 8,
                          }}
                        />
                        <span>RSMIN Traditional Territory</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HarvestingMapPage;
